/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs/RecordingTab.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Recording tab component for Conference form - Recording Options
────────────────────────────────────────────────────────────────*/

import { Disc } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import type { ConferenceRoom } from '../types';

interface RecordingTabProps {
  formData: ConferenceRoom;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: keyof ConferenceRoom, value: any) => void;
}

export function RecordingTab({ formData, onChange }: RecordingTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  const buildTooltipContent = (tooltipKey: string, debugKey?: string) => {
    const base = t(tooltipKey);
    if (!debugKey || !isDebugModeActive) {
      return base;
    }
    return `${base}\n\n${t('conferences:debug.field')}: ${t(debugKey)}`;
  };

  return (
    <div className="space-y-3">
      <FormBox
        title={t('conferences:sections.recording')}
        icon={<Disc className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordIncoming', 'conferences:debug.record_incoming')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordIncoming')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.recordIncoming}
              onChange={(value) => onChange('recordIncoming', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordOutgoing', 'conferences:debug.record_outgoing')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordOutgoing')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.recordOutgoing}
              onChange={(value) => onChange('recordOutgoing', value)}
            />
          </div>

          {/* Row 2 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordOnDemand', 'conferences:debug.record_on_demand')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordOnDemand')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.recordOnDemand}
              onChange={(value) => onChange('recordOnDemand', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordFileFormat', 'conferences:debug.recording_format')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordFileFormat')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.recordFileFormat}
            onChange={(e) => onChange('recordFileFormat', e.target.value as 'wav' | 'mp3' | 'ogg')}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          >
            <option value="wav">WAV</option>
            <option value="mp3">MP3</option>
            <option value="ogg">OGG</option>
          </select>

          {/* Row 3 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordFilePath', 'conferences:debug.record_path')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordFilePath')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.recordPath}
            onChange={(e) => onChange('recordPath', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder={t('conferences:fields.recordFilePathPlaceholder')}
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordFilenameTemplate', 'conferences:debug.recording_filename_template')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordFilenameTemplate')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.recordFilenameTemplate}
            onChange={(e) => onChange('recordFilenameTemplate', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder={t('conferences:fields.recordFilenameTemplatePlaceholder')}
          />

          {/* Row 4 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordModeratorOnly', 'conferences:debug.record_moderator_only')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordModeratorOnly')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.recordModeratorOnly}
              onChange={(value) => onChange('recordModeratorOnly', value)}
            />
          </div>
          <div></div>
          <div></div>
        </div>
      </FormBox>
    </div>
  );
}

